import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { ArrowLeft, Settings2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import FormCheckboxGroupField from 'components/IAP/formFields/FormCheckboxGroupField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import {
  formatAutomationRuleSummary,
  useOrgEventAutomationRules,
  useUpdateOrgEventAutomationRule,
  type OrgEventAutomationRule,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  AUTOMATION_CHANNEL_OPTIONS,
  AUTOMATION_PRIORITY_OPTIONS,
  buildNotifyConfigPayload,
  notifyFieldsFromRule,
  type AutomationNotifyFormFields,
} from '../EventsTab/automationNotifyForm';

function NotifyEditor({
  rule,
  onClose,
  onOpenEventSettings,
}: {
  rule: OrgEventAutomationRule;
  onClose: () => void;
  onOpenEventSettings: () => void;
}) {
  const groupGuid = useSelector(selectGroupGuid);
  const { data: rosters } = useRosters(groupGuid ? [groupGuid] : []);
  const updateRule = useUpdateOrgEventAutomationRule();

  const form = useForm<AutomationNotifyFormFields>({
    defaultValues: notifyFieldsFromRule(rule),
  });

  useEffect(() => {
    form.reset(notifyFieldsFromRule(rule));
  }, [rule, form]);

  const onSave = form.handleSubmit(async (values) => {
    await updateRule.mutateAsync({
      id: rule.id,
      notify_on_create: values.notifyOnCreate,
      notify_config: buildNotifyConfigPayload(values, rule.notify_config),
    });
    onClose();
  });

  return (
    <FormProvider {...form}>
      <form
        className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto tw:px-1"
        onSubmit={onSave}
      >
        <div>
          <p className="tw:m-0 tw:text-sm tw:font-medium">{rule.name}</p>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {formatAutomationRuleSummary(rule)}
          </p>
        </div>
        <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
          <StylishNewCheckbox
            checked={!!form.watch('notifyOnCreate')}
            onClick={() =>
              form.setValue('notifyOnCreate', !form.getValues('notifyOnCreate'))
            }
          />
          Notify on create
        </label>
        {form.watch('notifyOnCreate') && (
          <>
            <FormCheckboxGroupField
              title="Channels"
              fieldName="notificationTypes"
              options={[...AUTOMATION_CHANNEL_OPTIONS]}
            />
            <FormSelectField
              title="Recipients"
              fieldName="userIds"
              options={
                rosters?.map((u) => ({
                  label: u.email_address,
                  value: u.user_guid,
                })) || []
              }
              variant="full"
              isMulti
            />
            <FormSelectField
              title="Notification priority"
              fieldName="priority"
              options={[...AUTOMATION_PRIORITY_OPTIONS]}
              variant="full"
            />
            <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
              <StylishNewCheckbox
                checked={!!form.watch('confirmReceipt')}
                onClick={() =>
                  form.setValue(
                    'confirmReceipt',
                    !form.getValues('confirmReceipt')
                  )
                }
              />
              Request that Users Confirm Receipt
            </label>
          </>
        )}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="tw:h-auto tw:justify-start tw:px-0 tw:text-xs"
          onClick={onOpenEventSettings}
        >
          Edit thresholds in Automation Rules
        </Button>
        <div className="tw:mt-auto tw:flex tw:gap-2 tw:border-t tw:border-border tw:pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" size="sm" disabled={updateRule.isPending}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}

type NotificationAutomationSettingsPageProps = {
  onBack: () => void;
};

export function NotificationAutomationSettingsPage({
  onBack,
}: NotificationAutomationSettingsPageProps) {
  const [, setSearchParams] = useSearchParams();
  const { data: rules = [], isLoading } = useOrgEventAutomationRules({
    enabled: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingRule = rules.find((r) => r.id === editingId) ?? null;

  const openEventSettings = () => {
    setSearchParams(
      (params) => {
        params.set('openTab', 'Events');
        params.set('eventSettings', '1');
        params.set('from', 'notificationSettings');
        params.delete('notificationSettings');
        return params;
      },
      { replace: true }
    );
  };

  return (
    <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:bg-background tw:text-foreground">
      <div className="tw:flex tw:items-center tw:gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="tw:gap-1 tw:px-2"
          onClick={editingRule ? () => setEditingId(null) : onBack}
          aria-label={
            editingRule ? 'Back to settings' : 'Back to Notifications'
          }
        >
          <ArrowLeft className="tw:h-4 tw:w-4" />
          {editingRule ? 'Back to settings' : 'Back to Notifications'}
        </Button>
      </div>

      <div>
        <h2 className="tw:m-0 tw:text-sm tw:font-semibold">
          {editingRule ? 'Notification recipients' : 'Notification Settings'}
        </h2>
        {!editingRule ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            Choose who is notified when automation rules create org events.
            Thresholds are configured under Automation Rules.
          </p>
        ) : null}
      </div>

      {editingRule ? (
        <NotifyEditor
          rule={editingRule}
          onClose={() => setEditingId(null)}
          onOpenEventSettings={openEventSettings}
        />
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto">
          {isLoading ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Loading rules…
            </p>
          ) : rules.length === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              No automation rules yet. Create one in Events → Automation Rules.
            </p>
          ) : (
            rules.map((rule) => (
              <Item
                key={rule.id}
                variant="outline"
                size="xs"
                className="tw:cursor-pointer tw:bg-card tw:transition-colors tw:hover:bg-muted/40"
                onClick={() => setEditingId(rule.id)}
              >
                <ItemContent className="tw:gap-1">
                  <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                    <Badge
                      variant={rule.enabled ? 'default' : 'outline'}
                      className="tw:shrink-0"
                    >
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    {rule.notify_on_create ? (
                      <Badge variant="outline" className="tw:shrink-0">
                        Notify on
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="tw:shrink-0">
                        Notify off
                      </Badge>
                    )}
                  </div>
                  <ItemTitle className="tw:text-sm">{rule.name}</ItemTitle>
                  <ItemDescription className="tw:text-xs">
                    {(rule.notify_config?.userIds?.length ?? 0) > 0
                      ? `${rule.notify_config?.userIds?.length} recipient(s)`
                      : 'No recipients selected'}
                  </ItemDescription>
                </ItemContent>
                <ItemActions>
                  <Settings2 className="tw:h-4 tw:w-4 tw:text-muted-foreground" />
                </ItemActions>
              </Item>
            ))
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openEventSettings}
          >
            Open Automation Rules
          </Button>
        </div>
      )}
    </div>
  );
}
